import React, { useEffect, useContext, useState, useCallback } from "react";
import { API_PROGRAM_COURSE_ONLINE_STATUS } from "lib/endpoints/programs";
import { OBJECT_TYPE_ONLINE } from "../../../../constants.js";
import config from "lib/config";
import RestartCourse from "lib/components/MyCoursesCardView/restartCourse";
import { LinearProgress } from "Components/Base/Feedback/LinearProgress";

import {
  PROP_TYPE_PROGRAM_COURSE,
  PROP_DEFAULT_PROGRAM_COURSE,
} from "../../../../propTypes";
import LangContext from "../../../../langContext";
import ProgramContext from "../../ProgramContext";
import {
  getCourseOnlineLinkLabel,
  formatCourseDuration,
  StyledEmptyProgress,
  StyledEmptyTag,
  StyledEmptyStars,
  StyledEmptyDescription,
  reRegistrationPossible,
  GetRequestedCourseLink,
  GetRequestedOnlineCourseCatlogueLink,
} from "../../../../functions";
import { sanitize } from "lib/sanitizer.js";
import { AddLinebreaksRemoveHtmlTags } from "lib/utils";
import * as CONSTANTS from "../../../../constants";
import Description from "../../../Description";
import Rating from "lib/components/Rating";
import { GET } from "lib/api";
import { Box } from "Components/Base/Layout/Box";

const PERSONAL_ID = config.personalId;

const ProgramTypeOnline = (props) => {
  const languageContext = useContext(LangContext);
  const ProgramSettings = useContext(ProgramContext);
  const {
    course,
    setCompleted,
    isPortalOverview,
    programParticipant,
    isPreview,
  } = props;

  const [progress, setProgress] = useState(null);
  const [restartCourse, setRestartCourse] = useState(false);
  const [resetCompleted, setResetCompleted] = useState(false);
  const [rating, setRating] = useState(course.personalRating.rating);
  const [comment, setComment] = useState(course.personalRating.comment);
  const displayTime = formatCourseDuration(course);

  const description = sanitize(
    course.stepDescription || course.itemDescription,
  );
  const displayDescription =
    course.itemDescription &&
    course.itemDescription !== "" &&
    !isPortalOverview;

  const mobileViewport =
    window.matchMedia("(max-width: 400px)").matches || false;

  const fetchProgress = useCallback(async () => {
    const personalIdToFetchDataFor = programParticipant ?? PERSONAL_ID;
    const progress = GET(API_PROGRAM_COURSE_ONLINE_STATUS, {
      personalId: personalIdToFetchDataFor,
      courseId: course.courseId,
    });
    if (parseInt(progress) !== -1) {
      const progressToDisplay = course.completed ? 100 : parseInt(progress);
      setProgress(progressToDisplay);
    }
    setCompleted(course.completed);
  }, []);

  const goToCourse = (courseId) => {
    document.location.href =
      config.urlRoot +
      "education/entercourse.aspx?from=p&prid=" +
      luvit.GetQueryString("activityid") +
      "&courseid=" +
      courseId;
  };

  const updateRating = (newRating, newComment) => {
    course.personalRating.rating = newRating;
    course.personalRating.comment = newComment;
    setRating(newRating);
    setComment(newComment);
  };

  useEffect(() => {
    if (!isPortalOverview && course.isActiveStep && !isPreview) {
      fetchProgress();
    }
  }, [fetchProgress]);

  useEffect(() => {
    setCompleted(course.completed);
  }, [course.completed]);

  return (
    <div>
      {isPortalOverview && (
        <Rating
          currentRating={course.avgRating}
          noOfRatings={course.noOfRatings}
          readOnly={true}
        />
      )}
      {course.allowRating && !isPortalOverview ? (
        <Rating
          currentRating={course.personalRating.rating || 0}
          currentComment={course.personalRating.comment}
          type={OBJECT_TYPE_ONLINE}
          objectId={course.courseId}
          updateRating={updateRating}
          readOnly={false}
          managerView={ProgramSettings.managerView}
        />
      ) : (
        !isPortalOverview && <StyledEmptyStars />
      )}
      {displayDescription ? (
        <Description>
          <span
            title={AddLinebreaksRemoveHtmlTags(sanitize(description))}
            dangerouslySetInnerHTML={{
              __html: description,
            }}
          />
        </Description>
      ) : (
        !isPortalOverview && <StyledEmptyDescription />
      )}
      {isPortalOverview && (
        <div>
          <p className="elipssis">
            {`${languageContext("reason")}: `}
            <br />
            <span title={course.reason}>{course.reason}</span>
          </p>
        </div>
      )}
      {course.requested && (
        <span className="tag-warning">
          {languageContext("courserequested")}
        </span>
      )}

      {course.requested && !ProgramSettings.managerView ? (
        <GetRequestedCourseLink
          courseId={course.courseId}
          label={languageContext("application1")}
        />
      ) : (
        !isPortalOverview && <StyledEmptyTag className="card-empty-tag" />
      )}

      {progress
        ? !isPortalOverview && (
            <LinearProgress
              value={progress}
              colorOverride={CONSTANTS.COURSE_COLOR_PROGRESS_HIGH}
            />
          )
        : !isPortalOverview && <StyledEmptyProgress />}
      <Box
        display="flex"
        justifyContent={displayTime ? "space-between" : "center"}
        width="100%"
      >
        {displayTime && (
          <span>
            <span className="icon-time" aria-hidden="true" />
            &nbsp;{displayTime}
          </span>
        )}
        {((course.isActiveStep && !ProgramSettings.managerView) ||
          isPortalOverview) && (
          <>
            {!isPortalOverview ? (
              <div className={displayTime ? "card-time" : ""}>
                {!isPreview && (
                  <button
                    onClick={() => goToCourse(course.courseId)}
                    className={`button-transparent link ${
                      reRegistrationPossible(course) && "button--double-buttons"
                    }`}
                  >
                    {languageContext(getCourseOnlineLinkLabel(course, false))}
                  </button>
                )}
              </div>
            ) : (
              <GetRequestedOnlineCourseCatlogueLink
                courseId={course.courseId}
                label={languageContext("showactivity")}
                isPortalOverview={isPortalOverview}
              />
            )}
          </>
        )}
      </Box>

      {restartCourse && (
        <RestartCourse
          initiallyOpen={false}
          title={course.title}
          dialogStyles={{ minWidth: "450px" }}
          course={course}
          closeRestartCourse={() => setRestartCourse(false)}
          refresh={() => (course.completed = false)}
          personalId={PERSONAL_ID}
          isManagerOrAdmin={false}
        ></RestartCourse>
      )}
    </div>
  );
};

ProgramTypeOnline.propTypes = {
  course: PROP_TYPE_PROGRAM_COURSE,
};

ProgramTypeOnline.defaultProps = {
  course: PROP_DEFAULT_PROGRAM_COURSE,
};

export default ProgramTypeOnline;
